import { useEffect, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'

type CreateVaultBetaModalProps = {
  open: boolean
  onContinue: () => void
}

export function CreateVaultBetaModal({
  open,
  onContinue,
}: CreateVaultBetaModalProps) {
  const { t } = useTranslation()
  const titleId = useId()
  const ctaRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const id = window.setTimeout(() => ctaRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[550] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-[rgba(10,1,24,0.78)] backdrop-blur-sm"
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="border-line bg-surface-card/95 shadow-purple relative z-10 max-h-[min(85vh,720px)] w-full max-w-lg overflow-y-auto rounded-xl border p-6 backdrop-blur-[20px] md:p-8"
      >
        <div
          className="bg-gradient-brand pointer-events-none absolute inset-x-0 top-0 h-1 rounded-t-xl opacity-90"
          aria-hidden
        />
        <div className="mb-5 flex items-center gap-3 pt-1">
          <span className="bg-accent-muted text-accent rounded-full px-3 py-1 text-[0.6875rem] font-bold tracking-wider uppercase">
            {t('createVault.betaModal.badge')}
          </span>
          <div className="from-accent/50 h-px flex-1 bg-gradient-to-r to-transparent" />
        </div>
        <h2
          id={titleId}
          className="text-fg mb-4 text-xl leading-snug font-bold md:text-[1.35rem]"
        >
          {t('createVault.betaModal.title')}
        </h2>
        <div className="text-fg-secondary space-y-4 text-[0.9375rem] leading-relaxed">
          <p>{t('createVault.betaModal.body1')}</p>
          <p>{t('createVault.betaModal.body2')}</p>
          <p className="text-fg border-line mt-2 border-t pt-4 text-[0.9375rem] font-medium">
            {t('createVault.betaModal.closing')}
          </p>
        </div>
        <button
          ref={ctaRef}
          type="button"
          className="bg-gradient-brand text-fg-inverse shadow-glow hover:shadow-glow-lg mt-8 w-full cursor-pointer rounded-lg border-none px-6 py-3.5 text-base font-semibold transition-all hover:-translate-y-0.5 hover:opacity-95"
          onClick={onContinue}
        >
          {t('createVault.betaModal.cta')}
        </button>
      </div>
    </div>
  )
}
